import { useState } from 'react';

/**
 * Text that is said once it is finished (on leaving the field, or Enter on
 * one line), not at every key: each change to the builder is a step Undo can
 * take back, so typing a title is one change, not twenty. What the field shows
 * follows the tree when it changes underneath it (Undo, or the file loaded again).
 */
export function CommitText({
  value,
  onCommit,
  multiline = false,
  required = false,
  autoSelect = false,
  className = '',
  ...rest
}: {
  value: string;
  onCommit: (text: string) => void;
  multiline?: boolean;
  /** Blank is refused (a title); otherwise blank is said as blank. */
  required?: boolean;
  /** Focused and selected when it appears: a node just made, waiting for its title. */
  autoSelect?: boolean;
  className?: string;
  id?: string;
  placeholder?: string;
  maxLength?: number;
  'aria-label'?: string;
  rows?: number;
}) {
  const [text, setText] = useState(value);
  const [was, setWas] = useState(value);
  if (value !== was) {
    setWas(value);
    setText(value);
  }
  const commit = () => {
    const t = text.trim();
    if (required && !t) {
      setText(value);
      return;
    }
    if (t !== value.trim()) onCommit(t);
  };
  const common = {
    ...rest,
    className: `field ${className}`,
    value: text,
    autoFocus: autoSelect,
    onFocus: autoSelect ? (e: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => e.currentTarget.select() : undefined,
    onBlur: commit,
  };
  return multiline ? (
    <textarea {...common} onChange={(e) => setText(e.target.value)} />
  ) : (
    <input
      {...common}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setText(value);
        }
      }}
    />
  );
}
